/**
 * Role Management Utilities
 *
 * Roles:
 * - admin: เข้าถึง mfu-admin project ได้เท่านั้น
 * - admin_doctor: หมอที่เป็น admin (จาก MFU OpenAPI) - เข้าถึงหน้าสมุดสุขภาพและ template
 * - super_admin: เข้าถึงทุกหน้าใน mfu-SuperAdmin (level === '1')
 * - manager: เข้าถึงหน้า กลุ่มผู้รับการตรวจสุขภาพ และหน้า จัดการบริษัท
 * - analyze: เข้าถึงหน้า Dashboard องค์กร
 * - nurse (หรือ พยาบาล): เข้าถึงหน้า สมุดสุขภาพ
 */

// Role constants
export const ROLES = {
  ADMIN: 'admin',
  ADMIN_DOCTOR: 'admin_doctor',
  SUPER_ADMIN: 'super_admin',
  MANAGER: 'manager',
  ANALYZE: 'analyze',
  NURSE: 'nurse'
};

// Page access mapping
export const PAGE_ACCESS = {
  // Super Admin only pages
  '/template': [ROLES.SUPER_ADMIN, ROLES.ADMIN_DOCTOR],
  '/user-management': [ROLES.SUPER_ADMIN],

  // Manager pages (กลุ่มผู้รับการตรวจสุขภาพ, จัดการบริษัท)
  '/health-groups': [ROLES.SUPER_ADMIN, ROLES.MANAGER],
  '/campaigns': [ROLES.SUPER_ADMIN, ROLES.MANAGER],

  // Analyze pages (Dashboard องค์กร)
  '/dashboard-organization': [ROLES.SUPER_ADMIN, ROLES.ANALYZE],

  // Nurse pages (สมุดสุขภาพ, ประวัติการส่งผลสุขภาพ)
  '/healthbook': [ROLES.SUPER_ADMIN, ROLES.NURSE, ROLES.ADMIN_DOCTOR],
  '/health-records-history': [ROLES.SUPER_ADMIN, ROLES.NURSE, ROLES.ADMIN_DOCTOR],

  // Public pages
  '/': [ROLES.SUPER_ADMIN, ROLES.MANAGER, ROLES.ANALYZE, ROLES.NURSE, ROLES.ADMIN_DOCTOR],
  '/login': [ROLES.SUPER_ADMIN, ROLES.MANAGER, ROLES.ANALYZE, ROLES.NURSE, ROLES.ADMIN_DOCTOR]
};

/**
 * ตรวจสอบว่า user มี role ใดๆ ใน array หรือไม่
 * @param {Array} userRoles - Role array ของ user
 * @param {Array|string} requiredRoles - Role ที่ต้องการ (array หรือ string)
 * @returns {boolean}
 */
export const hasAnyRole = (userRoles, requiredRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }

  // ถ้าเป็น string ให้แปลงเป็น array
  const required = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

  return userRoles.some(role => required.includes(role));
};

/**
 * ตรวจสอบว่า user มี role ทั้งหมดที่ระบุหรือไม่
 * @param {Array} userRoles - Role array ของ user
 * @param {Array|string} requiredRoles - Role ที่ต้องการทั้งหมด
 * @returns {boolean}
 */
export const hasAllRoles = (userRoles, requiredRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }

  const required = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

  return required.every(role => userRoles.includes(role));
};

/**
 * ตรวจสอบว่า user สามารถเข้าถึงหน้านี้ได้หรือไม่
 * @param {Array} userRoles - Role array ของ user
 * @param {string} path - Path ของหน้า
 * @param {string} userLevel - Level ของ user จาก MFU OpenAPI (ไม่ได้ใช้ในการตรวจสอบสิทธิ์)
 * @returns {boolean}
 *
 * ⭐ Backend ใช้แค่ role array ในการตรวจสอบสิทธิ์ (ไม่ใช้ level)
 * - ถ้ามี super_admin หรือ admin role → เข้าถึงทุกหน้าได้
 * - ถ้าไม่มี → ตรวจสอบ role ตาม PAGE_ACCESS mapping
 */
export const canAccessPage = (userRoles, path, userLevel = null) => {
  // Super admin และ admin (dev/test) สามารถเข้าถึงทุกหน้าได้
  if (hasAnyRole(userRoles, [ROLES.SUPER_ADMIN, ROLES.ADMIN])) {
    return true;
  }

  // ตรวจสอบ path ที่กำหนดไว้
  const allowedRoles = PAGE_ACCESS[path];

  if (!allowedRoles) {
    // ถ้าไม่มี path นี้ใน mapping ให้อนุญาตเฉพาะ super admin และ admin
    return false;
  }

  return hasAnyRole(userRoles, allowedRoles);
};

/**
 * กรองเมนูตาม role ของ user
 * @param {Array} menuItems - รายการเมนูทั้งหมด
 * @param {Array} userRoles - Role array ของ user
 * @param {string} userLevel - Level ของ user จาก MFU OpenAPI
 * @returns {Array} - เมนูที่ user สามารถเข้าถึงได้
 */
export const filterMenuByRole = (menuItems, userRoles, userLevel = null) => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return [];
  }

  return menuItems.filter(item => {
    if (!item.path) return false;
    return canAccessPage(userRoles, item.path, userLevel);
  });
};

/**
 * ตรวจสอบว่า user เป็น Super Admin หรือไม่
 * @param {Array} userRoles - Role array ของ user
 * @returns {boolean}
 */
export const isSuperAdmin = (userRoles) => {
  return hasAnyRole(userRoles, ROLES.SUPER_ADMIN);
};

/**
 * ตรวจสอบว่า user เป็น Manager หรือไม่
 * @param {Array} userRoles - Role array ของ user
 * @returns {boolean}
 */
export const isManager = (userRoles) => {
  return hasAnyRole(userRoles, ROLES.MANAGER);
};

/**
 * ตรวจสอบว่า user เป็น Analyze หรือไม่
 * @param {Array} userRoles - Role array ของ user
 * @returns {boolean}
 */
export const isAnalyze = (userRoles) => {
  return hasAnyRole(userRoles, ROLES.ANALYZE);
};

/**
 * ตรวจสอบว่า user เป็น Nurse หรือไม่
 * @param {Array} userRoles - Role array ของ user
 * @returns {boolean}
 */
export const isNurse = (userRoles) => {
  return hasAnyRole(userRoles, ROLES.NURSE);
};

/**
 * ดึงหน้าแรกที่ user สามารถเข้าถึงได้
 * @param {Array} userRoles - Role array ของ user
 * @param {string} userLevel - Level ของ user จาก MFU OpenAPI (ไม่ได้ใช้ในการตรวจสอบสิทธิ์)
 * @returns {string} - Path ของหน้าแรก
 *
 * ⭐ Priority (สูงสุดไปต่ำสุด) - ใช้แค่ role array:
 * 1. Super Admin role -> /healthbook
 * 2. Admin -> /healthbook
 * 3. Admin Doctor -> /healthbook
 * 4. Manager -> /health-groups
 * 5. Analyze -> /dashboard-organization
 * 6. Nurse -> /healthbook
 */
export const getDefaultPage = (userRoles, userLevel = null) => {
  // ถ้าไม่มี role ให้กลับไป login
  if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) {
    return '/login';
  }

  // 1. Admin (dev/test user)
  if (hasAnyRole(userRoles, ROLES.ADMIN)) {
    return '/healthbook';
  }

  // 2. Super Admin role
  if (isSuperAdmin(userRoles)) {
    return '/healthbook';
  }

  // 3. Admin Doctor
  if (hasAnyRole(userRoles, ROLES.ADMIN_DOCTOR)) {
    return '/healthbook';
  }

  // 4. Manager
  if (isManager(userRoles)) {
    return '/health-groups';
  }

  // 5. Analyze
  if (isAnalyze(userRoles)) {
    return '/dashboard-organization';
  }

  // 6. Nurse
  if (isNurse(userRoles)) {
    return '/healthbook';
  }

  return '/login';
};
